import { cn } from '@april9au/react-common/utils';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Icon } from '@/components/ui/icon';

import { SectionHeader } from './section-header';

export type ResourceContactDetail = {
  label: string;
  value: string;
  href?: string;
};

export type ResourceItem = {
  id: string;
  title: string;
  description: string;
  href?: string;
  hrefLabel?: string;
  contact?: Array<ResourceContactDetail>;
};

/** A single external resource — title, contact details, description and an outbound link. */
export function ResourceCard({ item }: { item: ResourceItem }) {
  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>{item.title}</CardTitle>

        {item.contact?.length ? (
          <ul className="mt-1 flex flex-col gap-0.5">
            {item.contact.map((detail) => (
              <li key={detail.label} className="text-caption">
                {detail.href ? (
                  <a href={detail.href} className="hover:text-gold">
                    {detail.value}
                  </a>
                ) : (
                  detail.value
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </CardHeader>

      <CardContent>
        <CardDescription>{item.description}</CardDescription>
      </CardContent>

      {item.href ? (
        <CardFooter>
          <a
            href={item.href}
            target="_blank"
            rel="noreferrer"
            className="text-gold inline-flex items-center gap-1 text-sm font-semibold"
          >
            {item.hrefLabel ?? 'Visit Website'}
            <Icon name="heroicons/chevron-right-micro" />
          </a>
        </CardFooter>
      ) : null}
    </Card>
  );
}

/** Grid of `ResourceCard`s under a shared section header. Reusable for any local links/directory style page. */
export function ResourceListSection({
  id,
  eyebrow,
  heading,
  description,
  resources,
  className,
}: {
  id?: string;
  eyebrow?: string;
  heading: string;
  description?: string;
  resources: Array<ResourceItem>;
  className?: string;
}) {
  return (
    <section id={id} className={cn('section', className)}>
      <div className="container">
        <SectionHeader eyebrow={eyebrow} heading={heading} description={description} align="left" className="mb-12" />

        <div className={cn('grid grid-cols-1 gap-8', resources.length > 1 && 'lg:grid-cols-2')}>
          {resources.map((item) => (
            <ResourceCard key={item.id} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
}
